/**
 * `<Button />` e `<IconButton />` — DESIGN_SYSTEM §4.1.
 *
 * Regras que o componente **impoe**, e nao apenas documenta:
 * - Alvo de toque de 48 px por padrao (`size="md"`); `sm` tem 40 px visuais com a area
 *   estendida por `::before` ate 48 (§3.1). Nunca abaixo de 44 px.
 * - `loading` mantem a largura: evita salto de layout e clique acidental no vizinho (§4.1).
 * - Desabilitado **explica por que** (§3.11, item 19). Com `disabledReason`, o botao NAO usa
 *   o atributo `disabled` do DOM — ele continua focavel, com `aria-disabled`, para que o
 *   motivo seja lido. "Nunca so apagado."
 * - Sem `children` textual, `aria-label` e obrigatorio — pelo TIPO, nao por revisao de PR.
 */

import { forwardRef, useId, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { t } from '../content';
import { Icon, type IconName } from './Icon';
import { cx, idSeguro } from './util';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

type AtributosNativos = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'disabled' | 'type' | 'children' | 'aria-label'
>;

interface ButtonBase extends AtributosNativos {
  readonly variant?: ButtonVariant;
  /** `sm` = 40 px visuais (area 48), `md` = 48 px, `lg` = 56 px (§2.7). */
  readonly size?: ButtonSize;
  /** §4.1 — default `true` abaixo de 480 px para `primary`; o app decide o contexto. */
  readonly fullWidth?: boolean;
  readonly iconStart?: IconName;
  readonly iconEnd?: IconName;
  /** Troca o conteudo por spinner **mantendo a largura**. */
  readonly loading?: boolean;
  /** Anunciado durante o carregamento; default "Carregando". */
  readonly loadingLabel?: string;
  readonly disabled?: boolean;
  /**
   * Texto exibido quando `disabled`. **Obrigatorio na pratica**: nunca desabilite sem
   * explicar (§3.11, item 19). Com ele, o botao fica `aria-disabled` e continua focavel.
   */
  readonly disabledReason?: string;
  readonly type?: 'button' | 'submit';
  readonly className?: string;
}

/**
 * Sem texto visivel, `aria-label` e exigido pelo tipo. E a forma de o §4.1 ("Obrigatorio se
 * nao houver children textual. Lint verifica.") virar erro de compilacao em vez de achado de
 * revisao.
 */
export type ButtonProps =
  | (ButtonBase & { readonly children: ReactNode; readonly 'aria-label'?: string })
  | (ButtonBase & { readonly children?: undefined; readonly 'aria-label': string });

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    iconStart,
    iconEnd,
    loading = false,
    loadingLabel,
    disabled = false,
    disabledReason,
    type = 'button',
    className,
    children,
    ...resto
  },
  ref,
) {
  const idBruto = useId();
  const idMotivo = idSeguro(idBruto, 'motivo');
  const explicado = disabled && disabledReason !== undefined;

  const descritoPor = [resto['aria-describedby'], explicado ? idMotivo : undefined]
    .filter((valor): valor is string => typeof valor === 'string')
    .join(' ');

  return (
    <>
      <button
        {...resto}
        ref={ref}
        type={type}
        className={cx(
          'balcao-btn',
          `balcao-btn--${variant}`,
          `balcao-btn--${size}`,
          fullWidth && 'balcao-btn--full',
          loading && 'balcao-btn--loading',
          className,
        )}
        // §4.1: com motivo, o botao continua no fluxo de foco (`aria-disabled`) para que o
        // leitor de tela chegue nele e leia a explicacao. Sem motivo, `disabled` de verdade.
        disabled={disabled && !explicado}
        aria-disabled={disabled || loading ? true : undefined}
        aria-busy={loading ? true : undefined}
        aria-describedby={descritoPor === '' ? undefined : descritoPor}
      >
        {iconStart !== undefined && !loading ? <Icon name={iconStart} size={20} /> : null}
        <span className={cx(loading && 'balcao-btn__label--oculto')}>{children}</span>
        {iconEnd !== undefined && !loading ? <Icon name={iconEnd} size={20} /> : null}
        {loading ? (
          <span className="balcao-btn__spinner" role="status">
            <span className="balcao-sr-only">{loadingLabel ?? t('botao.carregando')}</span>
          </span>
        ) : null}
      </button>
      {explicado ? (
        <span id={idMotivo} className="balcao-btn__reason">
          {disabledReason}
        </span>
      ) : null}
    </>
  );
});

export type IconButtonProps = Omit<ButtonBase, 'iconStart' | 'iconEnd' | 'fullWidth'> & {
  readonly icon: IconName;
  /** Sempre obrigatorio: nao ha texto visivel para nomear a acao (§1.6). */
  readonly 'aria-label': string;
  readonly badge?: number;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, badge, className, ...resto },
  ref,
) {
  return (
    <Button {...resto} ref={ref} className={cx('balcao-icon-btn', className)}>
      <Icon name={icon} size={24} />
      {badge !== undefined && badge > 0 ? <span className="balcao-icon-btn__badge">{badge}</span> : null}
    </Button>
  );
});
